import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AttachmentPicker from "@/components/workspace/AttachmentPicker";
import { getSuggestedPrompts, getPromptPool } from "@/lib/suggestedPrompts";
import {
  Sparkles,
  Loader2,
  Type,
  ImageIcon,
  Film,
  Zap,
  Download,
  Copy,
  ArrowRight,
  Coins,
  Shield,
  Clock,
  MessageCircle,
  Wand2,
  Send,
  User as UserIcon,
  FileText,
  Mail,
  LayoutTemplate,
  Clapperboard,
  Megaphone,
  Handshake,
  Mic,
  MessageSquareQuote,
  Search,
  Cpu,
  ChevronRight,
  Activity,
  Layers,
  Palette,
  Bookmark,
  Share2,
  X as XIcon,
  Plus,
  Feather,
  BookOpen,
  Award,
  Flag,
  Newspaper,
  Package,
  Info,
  Anchor,
  Tag,
  RotateCw,
  Bell,
  Compass,
  Rocket,
  Target,
  Diamond,
  Users,
  Hash,
  Calendar,
  HelpCircle,
  MessageSquare,
  Route,
  Volume2,
  Video,
  Camera,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Static registries                                                          */
/* -------------------------------------------------------------------------- */
const TOOL_ICON = {
  FileText, Mail, LayoutTemplate, Clapperboard,
  Send, Megaphone, Handshake, Mic,
  Zap, MessageSquareQuote, Search, Sparkles,
  Feather, BookOpen, Award, Flag,
  Newspaper, Package, User: UserIcon, Info,
  Type, Anchor, Tag, Bell,
  Compass, MessageCircle, Rocket, Target, Diamond, Users,
  Hash, Calendar, HelpCircle, MessageSquare, Route,
  Film, ImageIcon, Volume2, Video, Camera,
};

const KIND_META = {
  text:  { label: "Text",  icon: Type,      accent: "primary" },
  image: { label: "Image", icon: ImageIcon, accent: "primary" },
  video: { label: "Video", icon: Film,      accent: "primary" },
  audio: { label: "Audio", icon: Volume2,   accent: "primary" },
};

const CATEGORY_LABEL = {
  long:   "Long-form",
  short:  "Short-form",
  punch:  "Punchy",
  brand:  "Brand",
  growth: "Growth",
  media:  "Media",
};

const CATEGORY_ORDER = ["long", "short", "punch", "brand", "growth", "media"];

const AGENT_FAMILY_ACCENT = {
  Claude: "bg-[#d97757]/10 text-[#d97757] border-[#d97757]/30",
  GPT:    "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  Gemini: "bg-sky-500/10 text-sky-700 border-sky-500/30",
};

const AGENT_FAMILY_DOT = {
  Claude: "bg-[#d97757]",
  GPT:    "bg-emerald-500",
  Gemini: "bg-sky-500",
};

const PROMPT_SEED = "Type anything — an idea, a topic, a wish. Draftwave takes it from here.";

const CHALLENGE_STEPS = [
  { at: 0,   label: "Idea",   desc: "Type one line" },
  { at: 60,  label: "Blog",   desc: "Long-form" },
  { at: 120, label: "Social", desc: "5 captions" },
  { at: 180, label: "Email",  desc: "1 send" },
  { at: 240, label: "SEO",    desc: "Meta pack" },
  { at: 300, label: "Visual", desc: "Header art" },
  { at: 360, label: "Video",  desc: "Cinematic clip" },
  { at: 480, label: "Ship",   desc: "Publish" },
];

/* -------------------------------------------------------------------------- */
/*  Utility hooks                                                              */
/* -------------------------------------------------------------------------- */
function useTypewriter(text, active) {
  const [t, setT] = useState("");
  useEffect(() => {
    if (!active) { setT(""); return; }
    let i = 0;
    setT("");
    const id = setInterval(() => {
      i += 1;
      if (i > text.length) { clearInterval(id); return; }
      setT(text.slice(0, i));
    }, 22);
    return () => clearInterval(id);
  }, [text, active]);
  return t;
}

/**
 * Endlessly rotates through a pool of prompts with a typewriter animation.
 * Types → pauses → erases → types the next → forever.
 *
 * Returns the current visible string; pass `active=false` (e.g. when the user
 * has typed something) to freeze the animation.
 */
function useRotatingTypewriter(pool, active, {
  typeMs = 24,
  eraseMs = 12,
  holdMs = 1400,
} = {}) {
  const [display, setDisplay] = useState("");
  const idxRef = useRef(0);
  useEffect(() => {
    if (!active || !pool || pool.length === 0) { setDisplay(""); return; }
    let cancelled = false;
    let timer;

    async function step() {
      while (!cancelled) {
        const phrase = pool[idxRef.current % pool.length];
        // Type
        for (let i = 1; i <= phrase.length && !cancelled; i++) {
          setDisplay(phrase.slice(0, i));
          await new Promise((r) => (timer = setTimeout(r, typeMs)));
        }
        if (cancelled) return;
        // Hold
        await new Promise((r) => (timer = setTimeout(r, holdMs)));
        if (cancelled) return;
        // Erase
        for (let i = phrase.length; i >= 0 && !cancelled; i--) {
          setDisplay(phrase.slice(0, i));
          await new Promise((r) => (timer = setTimeout(r, eraseMs)));
        }
        idxRef.current += 1;
      }
    }
    step();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pool.join("|")]);

  return display;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */
export default function Workspace() {
  const { user, refresh, loading } = useAuth();
  const [params, setParams] = useSearchParams();

  // Server-driven config
  const [config, setConfig] = useState(null);

  // Selection state
  const [kind, setKind] = useState("text");
  const [tool, setTool] = useState("blog");
  const [agentId, setAgentId] = useState("sonnet");

  // Video / Audio
  const [videoSeconds, setVideoSeconds] = useState(4);
  const [audioVoice, setAudioVoice] = useState("nova");
  const [pollingGenId, setPollingGenId] = useState(null);

  // Prompt + output
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [output, setOutput] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [hasWatermark, setHasWatermark] = useState(false);
  const [outAgentLabel, setOutAgentLabel] = useState(null);

  // Credits
  const [credits, setCredits] = useState({ credits: 0, weekly_refill: 5, costs: { text: 1, image: 5, video: 40, refine_tweak: 2 } });
  const [packs, setPacks] = useState([]);
  const [pollMsg, setPollMsg] = useState(null);

  // Challenge mode
  const [challengeMode, setChallengeMode] = useState(false);
  const [challengeStart, setChallengeStart] = useState(null);
  const [challengeElapsed, setChallengeElapsed] = useState(0);

  // Refine / thread
  const [generationId, setGenerationId] = useState(null);
  const [thread, setThread] = useState([]);
  const [refineMsg, setRefineMsg] = useState("");
  const [refineMode, setRefineMode] = useState("answer");
  const [refining, setRefining] = useState(false);

  // Presets
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [showPresetForm, setShowPresetForm] = useState(false);

  // Share
  const [sharing, setSharing] = useState(false);

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyKindFilter, setHistoryKindFilter] = useState("all");
  const [loadingGenId, setLoadingGenId] = useState(null);

  // Attachments (upload docs pinned to this prompt)
  const [attachments, setAttachments] = useState([]);

  // Unlock state — text/image are free to generate; download+share cost credits
  const [isUnlocked, setIsUnlocked] = useState(false);

  // Dream one up / Regenerate
  const [dreamBusy, setDreamBusy] = useState(false);
  const [lastGenerateParams, setLastGenerateParams] = useState(null);

  // Feature-on-homepage modal
  const [featureOpen, setFeatureOpen] = useState(false);
  const [featureTitle, setFeatureTitle] = useState("");
  const [featureDesc, setFeatureDesc] = useState("");
  const [featureBusy, setFeatureBusy] = useState(false);

  const promptRef = useRef(null);
  const threadEndRef = useRef(null);

  const promptPool = useMemo(() => getPromptPool(kind, tool), [kind, tool]);
  const typedPlaceholder = useRotatingTypewriter(promptPool, !prompt);

  /* -------- config bootstrap -------- */
  useEffect(() => {
    api.get("/workspace/config").then((r) => {
      setConfig(r.data);
      // Pick sensible defaults for kind
      const defAgent = r.data.default_agent_by_kind?.[kind] || r.data.agents.find(a => a.kind === kind)?.id;
      if (defAgent) setAgentId(defAgent);
    }).catch(() => {});
    api.get("/credits/packages").then((r) => setPacks(r.data.packages)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/credits/status").then((r) => setCredits(r.data)).catch(() => {});
      api.get("/workspace/presets").then((r) => setPresets(r.data.presets || [])).catch(() => {});
    }
  }, [user]);

  // When kind changes, reset agent to that kind's default and set default tool
  useEffect(() => {
    if (!config) return;
    const cur = config.agents.find(a => a.id === agentId);
    if (!cur || cur.kind !== kind) {
      const def = config.default_agent_by_kind?.[kind] || config.agents.find(a => a.kind === kind)?.id;
      if (def) setAgentId(def);
    }
    if (kind !== "text") return;
    // If current tool doesn't fit text list, pick first text tool
    const isText = (config.tools || []).some(t => t.id === tool && t.kind === "text");
    if (!isText) setTool("blog");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, config]);

  /* -------- challenge mode -------- */
  useEffect(() => { if (params.get("challenge") === "1") setChallengeMode(true); }, [params]);
  useEffect(() => {
    if (!challengeMode || !challengeStart) return;
    const id = setInterval(() => setChallengeElapsed(Math.floor((Date.now() - challengeStart) / 1000)), 500);
    return () => clearInterval(id);
  }, [challengeMode, challengeStart]);

  /* -------- credit poll post-checkout -------- */
  const creditSession = params.get("credit_session");
  const pollCredits = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 8) { setPollMsg({ kind: "error", message: "Timed out confirming purchase. Refresh in a moment." }); return; }
    try {
      const r = await api.get(`/credits/checkout/status/${sid}`);
      if (r.data.payment_status === "paid") {
        setPollMsg({ kind: "success", message: `${r.data.credits_added} credits added.` });
        toast.success(`${r.data.credits_added} credits added`);
        await refresh();
        const s = await api.get("/credits/status");
        setCredits(s.data);
        return;
      }
      if (r.data.status === "expired") { setPollMsg({ kind: "error", message: "Payment session expired." }); return; }
      setPollMsg({ kind: "pending", message: "Confirming with Stripe…" });
      setTimeout(() => pollCredits(sid, attempts + 1), 1800);
    } catch (e) { setPollMsg({ kind: "error", message: e?.response?.data?.detail || "Status check failed" }); }
  }, [refresh]);
  useEffect(() => {
    if (creditSession) {
      setPollMsg({ kind: "pending", message: "Confirming payment…" });
      pollCredits(creditSession);
      const cleaned = new URLSearchParams(params);
      cleaned.delete("credit_session"); cleaned.delete("pack");
      setParams(cleaned, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditSession]);

  /* -------- derived -------- */
  const agents = config?.agents || [];
  const agentsForKind = agents.filter(a => a.kind === kind);
  const tools = (config?.tools || []).filter(t => t.kind === "text");
  const toolsByCategory = useMemo(() => {
    const g = {};
    CATEGORY_ORDER.forEach((c) => (g[c] = []));
    tools.forEach(t => { (g[t.category] ||= []).push(t); });
    return g;
  }, [tools]);
  const currentAgent = agents.find(a => a.id === agentId) || null;

  const videoCostForSeconds = (s) => {
    const cfg = config?.video;
    if (!cfg) return credits.costs?.video ?? 40;
    return cfg.base_cost + Math.max(0, s - 4) * cfg.credit_per_second_over_5s;
  };

  const currentCost = kind === "video"
    ? videoCostForSeconds(videoSeconds)
    : kind === "audio"
      ? Math.max(6, Math.ceil((prompt.length || 1) / 200) * (credits.costs?.audio_per_200_chars ?? 6))
      : (credits.costs?.[kind] ?? (kind === "text" ? 1 : 5));

  const canGenerate = user && (credits.credits ?? 0) >= currentCost;
  const maxSecondsForPlan = config?.video?.max_seconds ?? 5;

  /* -------- actions -------- */
  async function handleFeatureSubmit() {
    if (!generationId) return;
    const title = featureTitle.trim();
    if (title.length < 3) { toast.error("Give it a title (at least 3 characters)."); return; }
    setFeatureBusy(true);
    try {
      const r = await api.post(`/workspace/generation/${generationId}/share-to-homepage`, {
        title,
        description: featureDesc.trim() || null,
      });
      if (r.data?.already_submitted) {
        toast.success(`Already submitted — status: ${r.data.status}`);
      } else {
        toast.success(r.data?.message || "Submitted for review ✨");
      }
      setFeatureOpen(false);
      setFeatureTitle("");
      setFeatureDesc("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit — try again.");
    } finally {
      setFeatureBusy(false);
    }
  }

  async function handleDreamPrompt() {
    if (!user) {
      localStorage.setItem("draftwave.workspace_pending", "1");
      window.location.href = "/login";
      return;
    }
    setDreamBusy(true);
    try {
      const r = await api.post("/workspace/dream-prompt", { kind, tool, seed: prompt.slice(0, 300) });
      const p = r.data?.prompt || "";
      if (p) {
        setPrompt(p);
        promptRef.current?.focus();
        toast.success("Fresh prompt dreamt up ✨");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't dream up a prompt.");
    } finally {
      setDreamBusy(false);
    }
  }

  async function handleRegenerate() {
    if (!lastGenerateParams) return;
    // Restore params silently then fire generate
    if (lastGenerateParams.prompt) setPrompt(lastGenerateParams.prompt);
    if (lastGenerateParams.agent) setAgentId(lastGenerateParams.agent);
    if (lastGenerateParams.kind) setKind(lastGenerateParams.kind);
    if (lastGenerateParams.tool) setTool(lastGenerateParams.tool);
    // Give React a tick to flush setState before firing
    setTimeout(() => handleGenerate(), 30);
  }

  async function handleGenerate() {
    if (!user) {
      localStorage.setItem("draftwave.workspace_pending", "1");
      window.location.href = "/login";
      return;
    }
    if (!prompt.trim()) { toast.error("Type a prompt first."); return; }
    if (!canGenerate) { toast.error(`You need ${currentCost} credits — top up to keep creating.`); return; }
    if (kind === "video" && videoSeconds > maxSecondsForPlan) {
      toast.error(`Your plan allows up to ${maxSecondsForPlan}s clips. Upgrade for longer.`);
      return;
    }
    setBusy(true);
    setOutput(null); setImageUrl(null); setVideoUrl(null); setAudioUrl(null);
    setGenerationId(null); setThread([]); setRefineMsg(""); setOutAgentLabel(null);
    setHasWatermark(false);
    setIsUnlocked(false);
    setLastGenerateParams({ kind, tool, agent: agentId, prompt, videoSeconds, audioVoice });
    try {
      const body = { kind, prompt, agent: agentId };
      if (kind === "text") body.tool = tool;
      if (kind === "video") body.duration_seconds = videoSeconds;
      if (kind === "audio") body.voice = audioVoice;
      if (attachments.length > 0) body.attachments = attachments.map((a) => a.id);
      const res = await api.post("/workspace/generate", body);

      if (kind === "text") setOutput(res.data.output);
      if (kind === "image") setImageUrl(res.data.image_data_url);
      if (kind === "audio") setAudioUrl(res.data.audio_data_url);
      if (res.data.id) setGenerationId(res.data.id);
      if (res.data.agent?.label) setOutAgentLabel(res.data.agent.label);
      if (typeof res.data.has_watermark === "boolean") setHasWatermark(res.data.has_watermark);

      if (kind === "text" && res.data.output) {
        setThread([
          { role: "user", content: prompt, mode: "generate" },
          { role: "assistant", content: res.data.output },
        ]);
        setAttachments([]);
        setRefineMode(/\?\s*$|\?[^\?]{0,180}$/m.test(res.data.output) ? "answer" : "tweak");
      }

      // Video is async — start polling
      if (kind === "video") {
        if (res.data.status === "processing" && res.data.id) {
          setPollingGenId(res.data.id);
          toast.info("Sora 2 is rendering — this usually takes 60-120s.");
        } else if (res.data.video_data_url) {
          setVideoUrl(res.data.video_data_url);
        }
      } else {
        toast.success(`Generated · ${res.data.credits_spent} credits`);
      }

      setCredits((c) => ({ ...c, credits: res.data.credits_remaining }));
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Generation failed";
      toast.error(String(msg));
    } finally {
      if (kind !== "video") setBusy(false);
    }
  }

  // Poll for async video job
  useEffect(() => {
    if (!pollingGenId) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await api.get(`/workspace/generation/${pollingGenId}`);
        if (r.data.status === "done" && r.data.video_data_url) {
          setVideoUrl(r.data.video_data_url);
          if (typeof r.data.has_watermark === "boolean") setHasWatermark(r.data.has_watermark);
          setBusy(false);
          setPollingGenId(null);
          toast.success("Video rendered ✨");
          return;
        }
        if (r.data.status === "failed") {
          setBusy(false);
          setPollingGenId(null);
          toast.error(r.data.error || "Video generation failed — credits refunded.");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (attempts > 60) {
        setBusy(false); setPollingGenId(null);
        toast.error("Timed out waiting for Sora. Refresh to try again.");
        return;
      }
      setTimeout(tick, 5000);
    };
    tick();
    return () => { cancelled = true; };
  }, [pollingGenId]);

  async function buyPack(code) {
    if (!user) { window.location.href = "/login"; return; }
    try {
      const r = await api.post("/credits/checkout", { package_code: code, origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
  }

  async function handleRefine() {
    if (!refineMsg.trim()) { toast.error("Write a reply first."); return; }
    if (!generationId) return;
    const willSpend = refineMode === "tweak" ? (credits.costs?.refine_tweak ?? 2) : 0;
    if (willSpend > 0 && (credits.credits ?? 0) < willSpend) {
      toast.error(`Tweak costs ${willSpend} credits — top up to keep refining.`); return;
    }
    setRefining(true);
    const optimistic = { role: "user", content: refineMsg, mode: refineMode, cost: willSpend };
    setThread((t) => [...t, optimistic]);
    const sent = refineMsg; setRefineMsg("");
    try {
      const res = await api.post("/workspace/refine", { generation_id: generationId, message: sent, mode: refineMode });
      setThread((t) => [...t, { role: "assistant", content: res.data.reply }]);
      if (res.data.canonical_updated) setOutput(res.data.reply);
      setCredits((c) => ({ ...c, credits: res.data.credits_remaining }));
      if (res.data.credits_spent > 0) toast.success(`Refined · ${res.data.credits_spent} credits`);
      else toast.success("Sent · free reply");
      setRefineMode(/\?\s*$|\?[^\?]{0,180}$/m.test(res.data.reply) ? "answer" : "tweak");
      await refresh();
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 80);
    } catch (e) {
      setThread((t) => t.slice(0, -1));
      setRefineMsg(sent);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Refine failed";
      toast.error(String(msg));
    } finally { setRefining(false); }
  }

  function applyPreset(p) {
    setKind(p.kind);
    if (p.kind === "text" && p.tool) setTool(p.tool);
    if (p.agent) setAgentId(p.agent);
    if (p.kind === "video" && p.duration_seconds) setVideoSeconds(p.duration_seconds);
    toast.success(`Preset "${p.name}" loaded`);
    promptRef.current?.focus();
  }

  async function savePreset() {
    if (!presetName.trim()) { toast.error("Give the preset a name."); return; }
    setSavingPreset(true);
    try {
      const body = { name: presetName.trim(), kind, agent: agentId };
      if (kind === "text") body.tool = tool;
      if (kind === "video") body.duration_seconds = videoSeconds;
      const r = await api.post("/workspace/presets", body);
      setPresets((ps) => [r.data, ...ps]);
      setPresetName("");
      setShowPresetForm(false);
      toast.success(`Saved preset "${r.data.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save preset");
    } finally { setSavingPreset(false); }
  }

  async function deletePreset(id) {
    try {
      await api.delete(`/workspace/presets/${id}`);
      setPresets((ps) => ps.filter((p) => p.id !== id));
      toast.success("Preset removed");
    } catch { toast.error("Failed to delete"); }
  }

  async function ensureUnlocked() {
    // Video + audio are pre-paid at generate-time. Only text + image gate.
    if (!generationId) return true;
    if (kind !== "text" && kind !== "image") return true;
    if (isUnlocked) return true;
    const unlockCost = kind === "text" ? 1 : 5;
    const ok = window.confirm(
      `Downloading or sharing a ${kind} generation costs ${unlockCost} credit${unlockCost === 1 ? "" : "s"}. Continue?`
    );
    if (!ok) return false;
    try {
      const r = await api.post(`/workspace/generation/${generationId}/unlock`);
      setIsUnlocked(true);
      if (r.data.credits_spent > 0) {
        setCredits((c) => ({ ...c, credits: r.data.credits_remaining }));
        toast.success(`Unlocked · -${r.data.credits_spent} credit${r.data.credits_spent === 1 ? "" : "s"}`);
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
      return false;
    }
  }

  async function handleShare() {
    if (!generationId) { toast.error("Nothing to share yet."); return; }
    if (!(await ensureUnlocked())) return;
    setSharing(true);
    try {
      const r = await api.post("/workspace/share", { generation_id: generationId });
      const url = `${window.location.origin}/share/${r.data.slug}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Share link copied", { description: url, duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally { setSharing(false); }
  }

  function copyOut() { if (output) { navigator.clipboard.writeText(output); toast.success("Copied"); } }
  async function download() {
    if (!(await ensureUnlocked())) return;
    let href, name;
    if (audioUrl) { href = audioUrl; name = "draftwave-audio.mp3"; }
    else if (videoUrl) { href = videoUrl; name = "draftwave-video.mp4"; }
    else if (imageUrl) { href = imageUrl; name = "draftwave-image.png"; }
    else if (output) { const b = new Blob([output], { type: "text/markdown" }); href = URL.createObjectURL(b); name = "draftwave-draft.md"; }
    else return;
    const a = document.createElement("a"); a.href = href; a.download = name;
    document.body.appendChild(a); a.click(); a.remove(); toast.success("Downloaded");
  }

  /* -------- history -------- */
  const fetchHistory = useCallback(async (kindFilter = "all") => {
    setHistoryLoading(true);
    try {
      const q = kindFilter && kindFilter !== "all" ? `?kind=${kindFilter}&limit=40` : "?limit=40";
      const r = await api.get(`/workspace/history${q}`);
      setHistoryItems(r.data.items || []);
    } catch (e) {
      toast.error("Couldn't load history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  function openHistory() {
    if (!user) { toast.error("Sign in to see your history"); return; }
    setHistoryOpen(true);
    fetchHistory(historyKindFilter);
  }

  async function loadGeneration(id) {
    setLoadingGenId(id);
    try {
      const r = await api.get(`/workspace/generation/${id}`);
      const g = r.data;
      // Reset output panels
      setOutput(null); setImageUrl(null); setVideoUrl(null); setAudioUrl(null);
      setThread([]); setRefineMsg("");
      // Switch UI to matching kind
      if (g.kind && g.kind !== kind) setKind(g.kind);
      if (g.tool) setTool(g.tool);
      if (g.agent_id) setAgentId(g.agent_id);
      if (g.agent_label) setOutAgentLabel(g.agent_label);
      setPrompt(g.prompt || "");
      setGenerationId(g.id);
      setHasWatermark(Boolean(g.has_watermark));
      setIsUnlocked(Boolean(g.unlocked) || g.kind === "video" || g.kind === "audio");
      if (g.kind === "text") {
        setOutput(g.output);
        // Rebuild refine thread if messages exist
        if (Array.isArray(g.messages) && g.messages.length) {
          setThread(g.messages.map(m => ({ role: m.role, content: m.content, mode: m.mode })));
        } else {
          setThread([{ role: "user", content: g.prompt || "" }, { role: "assistant", content: g.output || "" }]);
        }
      } else if (g.kind === "image") {
        setImageUrl(g.image_data_url || g.output);
      } else if (g.kind === "video") {
        if (g.status === "processing") {
          toast.info("This video is still rendering — will refresh when ready.");
          setPollingGenId(g.id);
          setBusy(true);
        } else {
          setVideoUrl(g.video_data_url || g.output);
        }
      } else if (g.kind === "audio") {
        setAudioUrl(g.audio_data_url || g.output);
      }
      setHistoryOpen(false);
      toast.success("Loaded from history");
      // Scroll to output area
      setTimeout(() => window.scrollTo({ top: 400, behavior: "smooth" }), 60);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open generation");
    } finally {
      setLoadingGenId(null);
    }
  }

  /* -------------------------------------------------------------------- */
  /*  RENDER                                                                */
  /* -------------------------------------------------------------------- */
  return (
    <div className="min-h-screen bg-background" data-testid="workspace-page">
      <MarketingNav />

      {/* ============ TOP HUD ============ */}
      <section className="border-b border-border bg-foreground text-background">
        <div className="max-w-[1500px] mx-auto flex flex-wrap items-stretch">
          <div className="flex items-center gap-3 px-5 py-3 border-r border-background/10">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="overline text-[10px] opacity-70">Draftwave workspace · v2.0</span>
          </div>
          <div className="hidden md:flex items-center gap-2 px-5 py-3 border-r border-background/10 font-mono text-[11px] uppercase tracking-widest opacity-70">
            <Cpu className="w-3 h-3 text-primary" />
            <span>{agents.length || "21"} agents · {tools.length || "40"} tools · Sora 2 · TTS</span>
          </div>
          <div className="hidden lg:flex items-center gap-2 px-5 py-3 border-r border-background/10 font-mono text-[11px] uppercase tracking-widest opacity-70">
            <Activity className="w-3 h-3 text-primary" />
            <span>Realtime</span>
          </div>
          <div className="ml-auto flex items-stretch">
            {user && (
              <button
                onClick={openHistory}
                className="flex items-center gap-2 px-5 py-3 border-l border-background/10 overline text-[10px] hover:bg-background hover:text-foreground transition-colors"
                data-testid="workspace-history-btn"
                title="Your past generations"
              >
                <Clock className="w-3 h-3" /> History
              </button>
            )}
            <div className="flex items-center gap-2 px-5 py-3 border-l border-background/10 font-mono text-[11px] uppercase tracking-widest">
              <Coins className="w-4 h-4 text-primary" />
              <span data-testid="workspace-credit-balance">{user ? `${credits.credits ?? 0}` : "—"} credits</span>
            </div>
            {user && (
              <a href="#credit-packs" className="px-5 py-3 border-l border-background/10 overline text-[10px] hover:bg-background hover:text-foreground transition-colors" data-testid="workspace-topup-link">
                Top up →
              </a>
            )}
          </div>
        </div>
      </section>

      {pollMsg && (
        <div
          className={`border-b border-border p-4 max-w-[1500px] mx-auto flex items-center gap-3 ${
            pollMsg.kind === "success" ? "bg-primary/5 text-primary" : pollMsg.kind === "error" ? "bg-destructive/5 text-destructive" : "bg-muted/40"
          }`}
          data-testid="credit-poll-status"
        >
          {pollMsg.kind === "pending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          <span className="text-sm">{pollMsg.message}</span>
        </div>
      )}

      {/* ============ 8-MIN CHALLENGE BANNER ============ */}
      {challengeMode && (
        <section className="border-b border-border bg-primary text-primary-foreground" data-testid="challenge-banner">
          <div className="max-w-[1500px] mx-auto p-4 md:p-6 flex flex-wrap items-center gap-4">
            <Clock className="w-5 h-5" />
            <div className="flex-1 min-w-[240px]">
              <div className="overline text-[10px] opacity-80">The 8-minute challenge</div>
              <div className="font-serif-display text-xl md:text-2xl leading-none mt-1">
                {challengeStart
                  ? `Elapsed · ${Math.floor(challengeElapsed / 60)}:${String(challengeElapsed % 60).padStart(2, "0")} / 08:00`
                  : "Press start. Ship a week of content before your coffee gets cold."}
              </div>
            </div>
            <div className="flex gap-2">
              {!challengeStart ? (
                <Button onClick={() => { setChallengeStart(Date.now()); setPrompt(PROMPT_SEED); }} className="rounded-none h-11 px-5 bg-foreground text-background hover:bg-foreground/90 font-mono uppercase tracking-widest text-xs" data-testid="challenge-start-btn">
                  Start · 08:00 <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={() => { setChallengeStart(null); setChallengeElapsed(0); }} variant="outline" className="rounded-none h-11 px-5 border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary font-mono uppercase tracking-widest text-xs" data-testid="challenge-reset-btn">
                  Reset
                </Button>
              )}
              <Button onClick={() => { setChallengeMode(false); setChallengeStart(null); const c = new URLSearchParams(params); c.delete("challenge"); setParams(c, { replace: true }); }} variant="outline" className="rounded-none h-11 px-5 border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary font-mono uppercase tracking-widest text-xs" data-testid="challenge-exit-btn">
                Exit
              </Button>
            </div>
          </div>
          {challengeStart && (
            <div className="max-w-[1500px] mx-auto grid grid-cols-4 md:grid-cols-8 gap-2 px-4 md:px-6 pb-4 md:pb-6">
              {CHALLENGE_STEPS.map((s) => {
                const done = challengeElapsed >= s.at;
                return (
                  <div key={s.label} className={`p-2 border font-mono text-[10px] uppercase tracking-widest ${done ? "bg-foreground text-background border-foreground" : "border-primary-foreground/30 opacity-70"}`} data-testid={`challenge-step-${s.label.toLowerCase()}`}>
                    <div>{String(Math.floor(s.at / 60)).padStart(2, "0")}:00</div>
                    <div className="mt-1">{s.label}</div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ============ TRY THIS ============ */}
      {!challengeMode && !output && !imageUrl && !videoUrl && !audioUrl && (
        <section className="border-b border-border bg-muted/30" data-testid="try-this-card">
          <div className="max-w-[1500px] mx-auto p-6 md:p-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 overline text-[10px] text-primary">
              <Sparkles className="w-3 h-3" /> Try this
            </div>
            <div className="flex-1 min-w-[240px] font-serif-display text-xl md:text-2xl leading-tight">
              "Create a 7-day marketing campaign for my business."
            </div>
            <Button onClick={() => { setPrompt(PROMPT_SEED); setKind("text"); setTool("blog"); promptRef.current?.focus(); }} variant="outline" className="rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs" data-testid="try-this-btn">
              Use this prompt <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </section>
      )}

      {/* ============ AGENT RAIL ============ */}
      <section className="border-b border-border overflow-hidden" data-testid="agent-rail">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-0">
          <div className="md:col-span-2 border-r border-border p-4 md:p-6">
            <div className="overline text-muted-foreground flex items-center gap-2">
              <Layers className="w-3 h-3 text-primary" /> Fleet
            </div>
            <div className="mt-3 font-serif-display text-2xl md:text-3xl leading-none">
              Pick your <span className="italic text-primary">agent.</span>
            </div>
            <div className="mt-3 text-[11px] font-mono text-muted-foreground">{agents.length} available · Sora 2 · 9 voices</div>
          </div>
          <div className="md:col-span-10 overflow-x-auto min-w-0">
            <div className="flex divide-x divide-border min-w-max">
              {agentsForKind.length === 0 && (
                <div className="p-6 overline text-muted-foreground">Loading agents…</div>
              )}
              {agentsForKind.map((a) => {
                const active = agentId === a.id;
                return (
                  <button
                    key={a.id}
                    onClick={() => setAgentId(a.id)}
                    className={`min-w-[190px] p-4 text-left transition-colors group relative ${
                      active ? "bg-foreground text-background" : "hover:bg-muted"
                    }`}
                    data-testid={`agent-${a.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${AGENT_FAMILY_DOT[a.family] || "bg-primary"} ${active ? "" : "opacity-80"}`} />
                      <span className="overline text-[9px] opacity-60">{a.family}</span>
                      {a.badge && (
                        <span className={`ml-auto px-1.5 py-0.5 border text-[8px] font-mono uppercase tracking-widest ${active ? "border-background/40" : AGENT_FAMILY_ACCENT[a.family] || "border-border"}`}>
                          {a.badge}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 font-medium text-sm leading-tight">{a.label}</div>
                    <div className="text-[10px] opacity-70 mt-1">{a.tagline}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRESETS RAIL ============ */}
      {user && (
        <section className="border-b border-border bg-muted/20" data-testid="presets-section">
          <div className="max-w-[1500px] mx-auto flex items-stretch flex-wrap">
            <div className="p-3 md:p-4 border-r border-border flex items-center gap-2 overline text-muted-foreground text-[10px]">
              <Bookmark className="w-3 h-3 text-primary" /> Presets · {presets.length}/24
            </div>
            <div className="flex-1 flex flex-wrap items-center gap-2 p-3 md:p-4">
              {presets.length === 0 && !showPresetForm && (
                <span className="text-[11px] font-mono text-muted-foreground">
                  No presets yet — save a favourite combo below.
                </span>
              )}
              {presets.map((p) => (
                <div key={p.id} className="group flex items-stretch border border-border" data-testid={`preset-${p.id}`}>
                  <button
                    onClick={() => applyPreset(p)}
                    className="px-3 py-1.5 text-[11px] font-mono hover:bg-foreground hover:text-background transition-colors flex items-center gap-2"
                    data-testid="preset-apply"
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                    <span className="max-w-[140px] truncate">{p.name}</span>
                    <span className="opacity-60 text-[9px] uppercase tracking-widest">
                      {p.kind}{p.tool ? `·${p.tool}` : ""}{p.duration_seconds ? `·${p.duration_seconds}s` : ""}
                    </span>
                  </button>
                  <button
                    onClick={() => deletePreset(p.id)}
                    className="px-2 border-l border-border hover:bg-destructive hover:text-destructive-foreground transition-colors"
                    aria-label="Delete preset"
                    data-testid="preset-delete"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {showPresetForm ? (
                <div className="flex items-stretch border border-primary" data-testid="preset-form">
                  <input
                    autoFocus
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="Preset name"
                    maxLength={60}
                    onKeyDown={(e) => { if (e.key === "Enter") savePreset(); if (e.key === "Escape") { setShowPresetForm(false); setPresetName(""); } }}
                    className="px-3 py-1.5 bg-background text-[11px] font-mono outline-none w-40"
                    data-testid="preset-name-input"
                  />
                  <button
                    onClick={savePreset}
                    disabled={savingPreset}
                    className="px-3 border-l border-primary bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest hover:opacity-90"
                    data-testid="preset-save-confirm"
                  >
                    {savingPreset ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                  </button>
                  <button
                    onClick={() => { setShowPresetForm(false); setPresetName(""); }}
                    className="px-2 border-l border-primary hover:bg-foreground hover:text-background"
                    aria-label="Cancel"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowPresetForm(true)}
                  className="flex items-center gap-1 border border-dashed border-border px-3 py-1.5 text-[11px] font-mono hover:border-primary hover:text-primary transition-colors"
                  data-testid="preset-save-btn"
                >
                  <Plus className="w-3 h-3" /> Save current
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============ MAIN CANVAS ============ */}
      <section className="border-b border-border">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          {/* ---- Kind + Tool sidebar ---- */}
          <aside className="lg:col-span-3 border-r border-border p-5 lg:p-7" data-testid="kind-tool-sidebar">
            <div className="overline text-muted-foreground">Format</div>
            <div className="mt-3 grid grid-cols-4 gap-0 border-l border-t border-border">
              {["text", "image", "video", "audio"].map((k) => {
                const M = KIND_META[k];
                const Icon = M.icon;
                const on = kind === k;
                return (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    className={`p-3 border-r border-b border-border transition-colors ${on ? "bg-foreground text-background" : "hover:bg-muted"}`}
                    data-testid={`kind-${k}`}
                  >
                    <Icon className="w-4 h-4 text-primary mx-auto" />
                    <div className="mt-2 text-[11px] font-medium">{M.label}</div>
                    <div className="text-[9px] opacity-60 mt-0.5 font-mono uppercase tracking-widest">
                      {k === "text" ? "1c" : k === "image" ? "5c" : k === "video" ? "40c+" : "6c+"}
                    </div>
                  </button>
                );
              })}
            </div>

            {kind === "text" && (
              <>
                <div className="mt-8 overline text-muted-foreground">Tool · {tools.length}</div>
                {CATEGORY_ORDER.map((cat) => {
                  const list = toolsByCategory[cat] || [];
                  if (!list.length) return null;
                  return (
                    <div key={cat} className="mt-4">
                      <div className="overline text-[9px] text-muted-foreground/60">{CATEGORY_LABEL[cat]}</div>
                      <div className="mt-2 grid grid-cols-2 gap-0 border-l border-t border-border">
                        {list.map((t) => {
                          const Icon = TOOL_ICON[t.icon] || Sparkles;
                          const on = tool === t.id;
                          return (
                            <button
                              key={t.id}
                              onClick={() => setTool(t.id)}
                              className={`p-3 text-left border-r border-b border-border transition-colors ${on ? "bg-primary/10 border-primary" : "hover:bg-muted"}`}
                              data-testid={`tool-${t.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <Icon className={`w-4 h-4 ${on ? "text-primary" : "text-foreground/70"}`} />
                                {on && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                              </div>
                              <div className="mt-2 text-[12px] font-medium">{t.label}</div>
                              <div className="text-[9px] opacity-70 mt-0.5 leading-tight">{t.tagline}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {kind === "video" && (
              <div className="mt-8" data-testid="video-duration">
                <div className="overline text-muted-foreground">Duration</div>
                <div className="mt-3 grid grid-cols-3 gap-0 border-l border-t border-border">
                  {(config?.video?.duration_options || [4, 8, 12]).map((s) => {
                    const on = videoSeconds === s;
                    const locked = s > maxSecondsForPlan;
                    return (
                      <button
                        key={s}
                        onClick={() => !locked && setVideoSeconds(s)}
                        disabled={locked}
                        className={`p-3 border-r border-b border-border font-mono text-xs transition-colors ${on ? "bg-foreground text-background" : locked ? "opacity-40 cursor-not-allowed" : "hover:bg-muted"}`}
                        data-testid={`video-secs-${s}`}
                      >
                        <div className="text-sm">{s}s</div>
                        <div className="text-[9px] opacity-60 mt-0.5">{videoCostForSeconds(s)}c</div>
                        {locked && <div className="mt-1 text-[8px] uppercase tracking-widest text-primary">Upgrade</div>}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 text-[10px] font-mono text-muted-foreground">
                  Plan cap · <span className="text-foreground">{maxSecondsForPlan}s</span> · <Link to="/pricing" className="underline hover:text-primary">upgrade</Link> for up to 12s cinematic clips
                </div>
              </div>
            )}

            {/* Privacy note */}
            <div className="mt-8 border-t border-border pt-6 flex items-start gap-3" data-testid="privacy-note">
              <Shield className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-[11px] font-mono text-muted-foreground leading-relaxed">
                Your prompts aren't sold. Your content isn't used to train models.{" "}
                <Link to="/legal/privacy" className="underline hover:text-primary" data-testid="privacy-link">Privacy →</Link>
              </div>
            </div>
          </aside>

          {/* ---- Prompt console ---- */}
          <div className="lg:col-span-4 border-r border-border p-5 lg:p-8">
            <div className="overline text-muted-foreground">Prompt console</div>
            <h1 className="font-serif-display text-3xl md:text-5xl leading-none tracking-tight mt-3">
              Type it.
              <br />
              <span className="italic text-primary">Watch it build.</span>
            </h1>

            {currentAgent && (
              <div className="mt-6 border border-border p-3 flex items-center gap-3" data-testid="active-agent-chip">
                <span className={`w-2 h-2 rounded-full ${AGENT_FAMILY_DOT[currentAgent.family] || "bg-primary"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-mono">Routing to <span className="font-medium">{currentAgent.label}</span></div>
                  <div className="overline text-[9px] text-muted-foreground truncate">{currentAgent.tagline}</div>
                </div>
                <span className="overline text-[9px] text-muted-foreground">{currentAgent.family}</span>
              </div>
            )}

            <div className="mt-5">
              <label className="overline text-muted-foreground">Prompt</label>
              <Textarea
                ref={promptRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={typedPlaceholder ? typedPlaceholder + "▍" : "Describe your idea. The studio does the rest."}
                className="rounded-none mt-2 min-h-[200px] font-mono text-sm"
                data-testid="workspace-prompt"
              />
              <div className="mt-2 text-[10px] font-mono text-muted-foreground flex justify-between">
                <span>{prompt.length} chars</span>
                <span>{kind === "text" ? `${tool} · ${currentCost}c` : kind === "image" ? `${currentCost}c` : kind === "video" ? `${videoSeconds}s · ${currentCost}c` : `voice · ${currentCost}c`}</span>
              </div>

              {/* Suggested prompt chips — fun, easy nudges for the everyday user */}
              {(() => {
                const suggestions = getSuggestedPrompts(kind, tool);
                if (!suggestions.length) return null;
                return (
                  <div className="mt-4" data-testid="suggested-prompts">
                    <div className="overline text-muted-foreground text-[10px] mb-2 flex items-center gap-2">
                      <Sparkles className="w-3 h-3 text-primary" /> Try one of these
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => { setPrompt(s); promptRef.current?.focus(); }}
                          className="text-left text-xs px-3 py-1.5 border border-border bg-background/60 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors max-w-full"
                          data-testid={`suggested-prompt-${i}`}
                          title="Click to use this prompt"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Attachments — images/videos/PDFs/docs for the agent to reference */}
              {user && (
                <AttachmentPicker
                  attachments={attachments}
                  onChange={setAttachments}
                  maxCount={(user?.plan === "pro" || user?.plan === "studio") ? 6 : 3}
                  disabled={busy}
                />
              )}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button
                onClick={handleGenerate}
                disabled={busy}
                className="rounded-none h-14 px-8 font-mono uppercase tracking-widest text-xs group flex-1 min-w-[240px]"
                data-testid="workspace-generate-btn"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : !user ? (
                  <>Sign in to generate <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" /></>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Generate · {currentCost}c</>
                )}
              </Button>
              {user && (
                <>
                  <Button
                    onClick={handleDreamPrompt}
                    disabled={busy || dreamBusy}
                    variant="outline"
                    className="rounded-none h-14 px-5 font-mono uppercase tracking-widest text-xs"
                    data-testid="workspace-dream-btn"
                    title="Let Draftwave dream up a prompt for you"
                  >
                    {dreamBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    <span className="ml-2">Dream one up</span>
                  </Button>
                  {lastGenerateParams && (
                    <Button
                      onClick={handleRegenerate}
                      disabled={busy}
                      variant="outline"
                      className="rounded-none h-14 px-5 font-mono uppercase tracking-widest text-xs"
                      data-testid="workspace-regenerate-btn"
                      title="Run the exact same prompt again"
                    >
                      <RotateCw className="w-4 h-4" />
                      <span className="ml-2">Regenerate</span>
                    </Button>
                  )}
                </>
              )}
            </div>
            {user && !canGenerate && (
              <a href="#credit-packs" className="mt-3 block overline text-primary hover-invert text-[11px]" data-testid="need-credits-cta">
                Need credits → Top up
              </a>
            )}

            {loading && <p className="mt-4 overline text-muted-foreground text-[10px]">Restoring session…</p>}
          </div>

          {/* ---- Live output ---- */}
          <div className="lg:col-span-5 relative min-h-[560px]" data-testid="live-output-panel">
            {/* Ambient background */}
            <div className="absolute inset-0 pointer-events-none opacity-70 bg-[radial-gradient(circle_at_top_right,hsl(var(--muted)/0.5),transparent_60%)]" />
            <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_right,hsl(var(--border)/0.35)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.35)_1px,transparent_1px)] bg-[size:32px_32px]" />

            <div className="relative">
              <div className="p-4 md:p-6 flex items-center justify-between border-b border-border/60 backdrop-blur-sm bg-background/40">
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                  <div className="overline text-muted-foreground">Live output</div>
                  {outAgentLabel && (
                    <div className="ml-2 border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
                      via {outAgentLabel}
                    </div>
                  )}
                </div>
                {(output || imageUrl || videoUrl || audioUrl) && (
                  <div className="flex gap-2">
                    {output && (
                      <Button onClick={copyOut} variant="outline" size="sm" className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest" data-testid="workspace-copy-btn">
                        <Copy className="w-3 h-3" /> Copy
                      </Button>
                    )}
                    <Button onClick={download} variant="outline" size="sm" className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest" data-testid="workspace-download-btn">
                      <Download className="w-3 h-3" /> Download
                    </Button>
                    {hasWatermark && (
                      <Link to="/pricing?upgrade=studio">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest border-primary text-primary hover:bg-primary hover:text-primary-foreground gap-1.5"
                          data-testid="workspace-remove-watermark-btn"
                          title="Watermark-free outputs unlock on Pro & Studio"
                        >
                          <span className="w-1.5 h-1.5 bg-primary rotate-45 inline-block" />
                          Remove watermark
                        </Button>
                      </Link>
                    )}
                    {generationId && (
                      <Button
                        onClick={handleShare}
                        disabled={sharing}
                        size="sm"
                        className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="workspace-share-btn"
                      >
                        {sharing ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Share2 className="w-3 h-3" /> Share</>}
                      </Button>
                    )}
                    {generationId && (
                      <Button
                        onClick={() => setFeatureOpen(true)}
                        size="sm"
                        variant="outline"
                        className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest"
                        data-testid="workspace-feature-btn"
                        title="Ask to be featured on the Draftwave homepage"
                      >
                        <Award className="w-3 h-3" /> Feature on homepage
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <div className="p-5 md:p-7 min-h-[520px] flex flex-col">
                {busy && (
                  <div className="flex flex-col items-center justify-center flex-1 gap-4 py-16" data-testid="workspace-busy">
                    <div className="relative">
                      <Loader2 className="w-12 h-12 animate-spin text-primary" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="w-2 h-2 bg-primary rounded-full" />
                      </div>
                    </div>
                    <div className="overline text-muted-foreground">
                      {kind === "video" ? `Rendering ${videoSeconds}s cinematic clip…` : kind === "image" ? "Painting your image…" : kind === "audio" ? `Recording via ${currentAgent?.label || "voice"}…` : `Drafting via ${currentAgent?.label || "the fleet"}…`}
                    </div>
                    {kind === "video" && <p className="text-xs text-muted-foreground max-w-sm text-center">Sora 2 renders in ~60-120s. Feel free to browse — we'll notify you.</p>}
                  </div>
                )}

                {!busy && !output && !imageUrl && !videoUrl && !audioUrl && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center gap-5 py-12">
                    <div className="w-16 h-16 border border-border flex items-center justify-center bg-background/60">
                      {kind === "text" && <Type className="w-6 h-6 text-primary" />}
                      {kind === "image" && <ImageIcon className="w-6 h-6 text-primary" />}
                      {kind === "video" && <Film className="w-6 h-6 text-primary" />}
                      {kind === "audio" && <Volume2 className="w-6 h-6 text-primary" />}
                    </div>
                    <div className="font-serif-display text-2xl md:text-3xl">Your {kind} shows up here.</div>
                    <p className="text-sm text-muted-foreground max-w-sm">
                      Pick a mode. Type a prompt. Ship it in seconds.
                    </p>
                    <div className="flex items-center gap-2 overline text-[10px] text-muted-foreground">
                      <ChevronRight className="w-3 h-3" /> {currentAgent?.label || "Any agent"} <span className="opacity-40">·</span> {kind === "text" ? tool : kind} <span className="opacity-40">·</span> {currentCost}c
                    </div>
                  </div>
                )}

                {output && (
                  <div className="flex-1 flex flex-col gap-3 max-h-[70vh] overflow-auto" data-testid="workspace-thread">
                    {thread.length === 0 ? (
                      <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed bg-background border border-border p-5" data-testid="workspace-text-output">
                        {output}
                      </pre>
                    ) : (
                      <>
                        {thread.map((m, i) => (
                          <div key={i} className={`border p-4 ${m.role === "user" ? "border-primary/40 bg-primary/5" : "border-border bg-background"}`} data-testid={`thread-${m.role}-${i}`}>
                            <div className="flex items-center justify-between overline text-[10px] opacity-70 mb-2">
                              <div className="flex items-center gap-2">
                                {m.role === "user" ? <UserIcon className="w-3 h-3" /> : <Sparkles className="w-3 h-3 text-primary" />}
                                <span>{m.role === "user" ? "You" : (outAgentLabel || "Draftwave")}</span>
                                {m.mode && m.role === "user" && (
                                  <span className="ml-2 px-1.5 py-0.5 border border-current text-[9px]">
                                    {m.mode === "generate" ? "prompt" : m.mode}
                                  </span>
                                )}
                              </div>
                              {m.cost != null && m.role === "user" && (
                                <span className="font-mono">{m.cost > 0 ? `-${m.cost}c` : "free"}</span>
                              )}
                            </div>
                            <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
                              {m.content}
                            </pre>
                          </div>
                        ))}
                        <div ref={threadEndRef} />
                      </>
                    )}
                  </div>
                )}

                {imageUrl && (
                  <div className="flex-1 flex items-center justify-center py-4" data-testid="workspace-image-output">
                    <img src={imageUrl} alt="Generated" className="max-h-[560px] w-auto border border-border shadow-lg" />
                  </div>
                )}

                {videoUrl && (
                  <div className="flex-1 flex items-center justify-center py-4" data-testid="workspace-video-output">
                    <video controls className="max-h-[560px] w-auto border border-border shadow-lg" poster="">
                      <source src={videoUrl} type="video/mp4" />
                    </video>
                  </div>
                )}

                {audioUrl && (
                  <div className="flex-1 flex flex-col items-center justify-center py-6 gap-4" data-testid="workspace-audio-output">
                    <div className="w-full max-w-lg border border-border bg-background p-6">
                      <div className="overline text-[10px] text-muted-foreground flex items-center gap-2 mb-3">
                        <Volume2 className="w-3 h-3 text-primary" /> {outAgentLabel || "Voice"} · MP3
                      </div>
                      <audio controls className="w-full" data-testid="audio-player">
                        <source src={audioUrl} type="audio/mpeg" />
                      </audio>
                      <div className="mt-3 pt-3 border-t border-border font-mono text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap max-h-32 overflow-auto">
                        {prompt}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ REFINE / REPLY COMPOSER ============ */}
      {output && kind === "text" && generationId && (
        <section className="border-b border-border" data-testid="refine-section">
          <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-5 border-r border-border p-6 lg:p-10">
              <div className="overline text-muted-foreground flex items-center gap-2">
                <MessageCircle className="w-3 h-3 text-primary" /> Reply to the output
                {thread.length > 2 && (
                  <span className="ml-2 border border-border px-2 py-0.5 font-mono text-[9px]" data-testid="refine-turn-badge">
                    Turn {Math.max(1, Math.floor(thread.length / 2))} · chain memory on
                  </span>
                )}
              </div>
              <h2 className="font-serif-display text-3xl md:text-5xl leading-none tracking-tight mt-3">
                Answer.
                <br />
                <span className="italic text-primary">Or tweak.</span>
              </h2>
              <p className="mt-4 text-sm text-muted-foreground max-w-md">
                Answering a question Draftwave asked? Free. Want a rewrite, addition or a specific
                change? <span className="font-mono text-foreground">{credits.costs?.refine_tweak ?? 2} credits</span> per tweak.
              </p>

              <div className="mt-8 grid grid-cols-2 border-l border-t border-border">
                <button
                  onClick={() => setRefineMode("answer")}
                  className={`p-4 text-left border-r border-b border-border transition-colors ${refineMode === "answer" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                  data-testid="refine-mode-answer"
                >
                  <div className="flex items-center justify-between">
                    <MessageCircle className="w-4 h-4 text-primary" />
                    <span className="overline text-[9px] opacity-60">Free</span>
                  </div>
                  <div className="mt-2 font-medium">Answer</div>
                  <div className="text-[10px] opacity-70 mt-0.5">Reply to a question</div>
                </button>
                <button
                  onClick={() => setRefineMode("tweak")}
                  className={`p-4 text-left border-r border-b border-border transition-colors ${refineMode === "tweak" ? "bg-foreground text-background" : "hover:bg-muted"}`}
                  data-testid="refine-mode-tweak"
                >
                  <div className="flex items-center justify-between">
                    <Wand2 className="w-4 h-4 text-primary" />
                    <span className="overline text-[9px] opacity-60">{credits.costs?.refine_tweak ?? 2}c</span>
                  </div>
                  <div className="mt-2 font-medium">Tweak</div>
                  <div className="text-[10px] opacity-70 mt-0.5">Rewrite, add, change</div>
                </button>
              </div>
            </div>

            <div className="lg:col-span-7 p-6 lg:p-10 bg-muted/30 flex flex-col gap-4">
              <label className="overline text-muted-foreground">
                {refineMode === "answer" ? "Your answer (free · ≤ 400 chars)" : `Your request (${credits.costs?.refine_tweak ?? 2} credits)`}
              </label>
              <Textarea
                value={refineMsg}
                onChange={(e) => setRefineMsg(e.target.value)}
                placeholder={refineMode === "answer" ? "Type your reply to Draftwave's question…" : "e.g. Make it warmer, cut 30 words, add a Silverlake reference and a rainbow emoji."}
                maxLength={refineMode === "answer" ? 400 : 4000}
                className="rounded-none min-h-[140px] font-mono text-sm"
                data-testid="refine-input"
              />
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="overline text-[10px] text-muted-foreground">
                  {refineMode === "answer" ? `${refineMsg.length}/400 · free reply` : `Will spend ${credits.costs?.refine_tweak ?? 2} credits · balance ${credits.credits ?? 0}`}
                </div>
                <Button onClick={handleRefine} disabled={refining || !refineMsg.trim()} className="rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs" data-testid="refine-send-btn">
                  {refining ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <Send className="w-4 h-4" />
                      {refineMode === "answer" ? "Send reply · free" : `Send tweak · ${credits.costs?.refine_tweak ?? 2}c`}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ CREDIT PACKS ============ */}
      <section id="credit-packs" className="border-b border-border">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-4 border-r border-border p-8 md:p-12">
            <div className="overline text-muted-foreground flex items-center gap-2">
              <Coins className="w-3 h-3" /> Credits
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-none tracking-tight mt-4">
              Refill.
              <br />
              <span className="italic text-primary">Compound.</span>
            </h2>
            <p className="mt-6 text-sm text-muted-foreground">
              Credits never expire. Text costs {credits.costs?.text || 1}c, image {credits.costs?.image || 5}c, video {credits.costs?.video || 40}c+.
              First-time signup gets 120 free credits + {credits.weekly_refill || 5} weekly on the house.
            </p>
            {!user && (
              <Link to="/login" className="mt-6 inline-block">
                <Button className="rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs" data-testid="pack-signup-cta">
                  Sign up → 120 free credits
                </Button>
              </Link>
            )}
          </div>
          <div className="md:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-border">
              {packs.map((p, i) => {
                const dark = i === 2 || i === 3;
                return (
                  <div key={p.code} className={`p-6 md:p-8 border-r border-b border-border ${dark ? "bg-foreground text-background" : ""}`} data-testid={`credit-pack-${p.code}`}>
                    <div className="flex items-center justify-between">
                      <div className="overline opacity-60">{p.name}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">{p.cost_per_credit_display}/credit</div>
                    </div>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="font-serif-display text-4xl md:text-5xl leading-none">{p.price_display}</span>
                      <span className="text-sm opacity-60">one-time</span>
                    </div>
                    <div className="mt-3 font-mono text-sm">
                      {p.credits.toLocaleString()} credits
                      {p.bonus > 0 && <span className="text-primary"> + {p.bonus} bonus</span>}
                    </div>
                    <div className="mt-1 text-xs opacity-70">{p.tagline}</div>
                    {p.audience && (
                      <div className="mt-3 overline text-[10px] opacity-50 border-t border-current/10 pt-3">{p.audience}</div>
                    )}
                    <Button onClick={() => buyPack(p.code)} className={`mt-6 rounded-none h-11 px-5 font-mono uppercase tracking-widest text-xs w-full ${dark ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`} data-testid={`buy-pack-${p.code}`}>
                      {user ? "Buy pack" : "Sign in to buy"} <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURE STRIP ============ */}
      <section className="border-b border-border">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-3">
          {[
            { icon: Cpu,   title: "25 agents. One console.",     body: "Draftwave Nova ∞ (unified cinema engine), Claude Sonnet & Opus, GPT-4o & GPT-5, Gemini Pro & Flash, GPT Image 1, Nano Banana, Sora 2, and 9 studio-grade voices — one workspace, one voice." },
            { icon: Layers, title: "40 tools · one voice.",       body: "Long-form, short-form, punchy, brand, growth, media — 40 tools across six shelves, all sharing the same editorial voice." },
            { icon: Palette,title: "Cinematic clips · up to 12s", body: "Sora 2 renders 4/8/12-second cinematic clips. Studio plan unlocks the longest tier. Credits scale with duration." },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className={`p-8 md:p-12 ${i < 2 ? "border-r border-border" : ""}`}>
                <Icon className="w-6 h-6 text-primary" />
                <h3 className="font-serif-display text-2xl md:text-3xl mt-6 leading-tight">{f.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />

      {/* ============ HISTORY DRAWER ============ */}
      {historyOpen && (
        <>
          <div
            className="fixed inset-0 z-[100] bg-foreground/60 backdrop-blur-sm"
            onClick={() => setHistoryOpen(false)}
            data-testid="workspace-history-overlay"
          />
          <aside
            className="fixed top-0 right-0 z-[101] h-full w-full max-w-md bg-background border-l border-border shadow-2xl flex flex-col"
            data-testid="workspace-history-drawer"
          >
            <header className="p-5 border-b border-border flex items-center gap-3">
              <Clock className="w-4 h-4 text-primary" />
              <div className="flex-1">
                <div className="overline text-[10px] text-muted-foreground">Studio archive</div>
                <div className="font-serif-display text-2xl leading-none">Your history</div>
              </div>
              <button
                onClick={() => setHistoryOpen(false)}
                className="rounded-none border border-border h-9 w-9 flex items-center justify-center hover:bg-foreground hover:text-background transition-colors"
                data-testid="workspace-history-close"
                aria-label="Close history"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </header>

            <div className="p-4 border-b border-border flex gap-2 flex-wrap" data-testid="workspace-history-filters">
              {[
                { id: "all", label: "All" },
                { id: "text", label: "Text" },
                { id: "image", label: "Image" },
                { id: "video", label: "Video" },
                { id: "audio", label: "Audio" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => { setHistoryKindFilter(f.id); fetchHistory(f.id); }}
                  className={`overline text-[10px] px-3 py-1.5 border border-border transition-colors ${
                    historyKindFilter === f.id ? "bg-foreground text-background" : "hover:bg-foreground hover:text-background"
                  }`}
                  data-testid={`workspace-history-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto" data-testid="workspace-history-list">
              {historyLoading && (
                <div className="p-8 flex items-center justify-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              )}
              {!historyLoading && historyItems.length === 0 && (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  Nothing here yet. Your generations show up automatically.
                </div>
              )}
              {!historyLoading && historyItems.map((g) => {
                const KindIcon = (KIND_META[g.kind]?.icon) || FileText;
                const busy = loadingGenId === g.id;
                const when = g.created_at ? new Date(g.created_at) : null;
                return (
                  <button
                    key={g.id}
                    onClick={() => loadGeneration(g.id)}
                    disabled={busy}
                    className="w-full text-left p-4 border-b border-border/60 hover:bg-primary/5 transition-colors flex gap-3 disabled:opacity-60"
                    data-testid={`workspace-history-item-${g.id}`}
                  >
                    <div className="mt-0.5 w-8 h-8 border border-border flex items-center justify-center bg-background">
                      <KindIcon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="overline text-[10px] text-primary">{g.kind}</span>
                        {g.tool && (
                          <span className="overline text-[10px] text-muted-foreground">· {g.tool}</span>
                        )}
                        {g.status === "processing" && (
                          <span className="overline text-[10px] text-amber-600">· rendering…</span>
                        )}
                        {g.status === "failed" && (
                          <span className="overline text-[10px] text-destructive">· failed</span>
                        )}
                      </div>
                      <div className="text-sm font-medium leading-snug mt-1 line-clamp-2">
                        {g.prompt || g.preview || "(no prompt)"}
                      </div>
                      {g.kind === "text" && g.preview && (
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2 italic">
                          {g.preview}…
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        {g.agent_label && <span>{g.agent_label}</span>}
                        {g.credits_spent ? <span>· {g.credits_spent}c</span> : null}
                        {when && <span>· {when.toLocaleDateString()} {when.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>}
                      </div>
                    </div>
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin text-primary self-center" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground self-center" />
                    )}
                  </button>
                );
              })}
            </div>

            <footer className="p-3 border-t border-border text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center">
              Showing latest {historyItems.length} · click any item to view, download or share
            </footer>
          </aside>
        </>
      )}

      {/* -------- Feature on homepage modal -------- */}
      {featureOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur"
          onClick={() => !featureBusy && setFeatureOpen(false)}
          data-testid="feature-modal-overlay"
        >
          <div
            className="w-full max-w-lg border border-border bg-background p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="feature-modal"
          >
            <div className="overline text-primary mb-2 flex items-center gap-2">
              <Award className="w-4 h-4" /> Feature on the Draftwave homepage
            </div>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mb-2">
              Your creation could headline the site.
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Submissions go to the Draftwave editors for review. If approved, your creation is
              showcased in the Featured Creations wall on <span className="font-mono">draftwave.online</span> —
              credited to your username.
            </p>
            <label className="block mb-4">
              <div className="overline text-xs mb-2">Title *</div>
              <input
                type="text"
                value={featureTitle}
                onChange={(e) => setFeatureTitle(e.target.value)}
                maxLength={120}
                placeholder="Give this creation a headline"
                className="w-full px-4 py-3 border border-border bg-background focus:outline-none focus:border-primary transition"
                data-testid="feature-title-input"
              />
            </label>
            <label className="block mb-6">
              <div className="overline text-xs mb-2">Description (optional)</div>
              <textarea
                value={featureDesc}
                onChange={(e) => setFeatureDesc(e.target.value)}
                maxLength={600}
                rows={3}
                placeholder="A sentence or two of context for the editors"
                className="w-full px-4 py-3 border border-border bg-background focus:outline-none focus:border-primary transition resize-none"
                data-testid="feature-desc-input"
              />
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right">
                {featureDesc.length}/600
              </div>
            </label>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setFeatureOpen(false)}
                disabled={featureBusy}
                className="rounded-none"
                data-testid="feature-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleFeatureSubmit}
                disabled={featureBusy || featureTitle.trim().length < 3}
                className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="feature-submit-btn"
              >
                {featureBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
                Submit for review
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
